<template>
  <transition
    :name="noAnimation ? 'none': transitionClasses"
  >
    <div
      v-if="value"
      :class="[{'is--full': isFull, 'is--small': isSmall, 'is--large': isLarge, 'is--large-content': largeContent}, baseModalClass]"
      class="BaseModal modal modal__backdrop"
      @click.self="onCloseClick"
    >
      <div
        ref="modal"
        aria-labelledby="modal-headline"
        aria-modal="true"
        class="modal__container"
        :class="{'is--no-animation': noAnimation}"
        role="dialog"
      >
        <div
          :class="[{'modal__header-solid': isShowTitle},'modal__header']"
        >
          <h3
            v-if="isShowTitle"
            class="modal__header-title"
          >
            <slot name="title" />
          </h3>
          <button
            v-if="!hideCloseButton"
            aria-label="Close modal"
            class="modal__close-button"
            type="button"
            @click="onCloseClick"
          >
            <CloseModalIcon class="modal__close-button-icon svg-icon close-icon" />
          </button>
        </div>
        <div class="modal__content">
          <slot />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import CloseModalIcon from 'images/svg-icons/fa-close.svg';
import CONFIG from 'Config';
import { generateIdShort } from 'Utils/common';

export default {
  name: 'BaseModal',
  components: {
    CloseModalIcon,
  },
  props: {
    value: Boolean,
    size: {
      default: 'full',
      type: String,
      validator: value => ['full', 'small', 'large'].includes(value),
    },
    hideCloseButton: Boolean,
    transition: {
      type: String,
      default: 'scale-in-center',
    },
    noAnimation: Boolean,
    largeContent: Boolean,
  },
  data() {
    return {
      generatedId: String,
    };
  },
  computed: {
    isFull() {
      return this.size === 'full';
    },
    isSmall() {
      return this.size === 'small';
    },
    isLarge() {
      return this.size === 'large';
    },
    isShowTitle() {
      return Boolean(this.$slots.title);
    },
    transitionClasses() {
      return this.isFull ? 'fade-in-up' : this.transition;
    },
  },
  mounted() {
    this.generatedId = generateIdShort();
    this.addClassOnOpenModal(`${ CONFIG.modalOpenClass }--${ this.generatedId }`);
  },
  beforeDestroy() {
    this.removeClassesOnCloseModal(`${ CONFIG.modalOpenClass }--${ this.generatedId }`);
  },
  methods: {
    onCloseClick() {
      this.$emit('close');
    },
    addClassOnOpenModal(className = CONFIG.modalOpenClass) {
      const html = document.getElementsByTagName('html')[0];
      html.classList.add(className);
    },
    removeClassesOnCloseModal(className = CONFIG.modalOpenClass) {
      const html = document.getElementsByTagName('html')[0];
      html.classList.remove(className);
    },

  },
  staticData: {
    baseModalClass: CONFIG.baseModalClass,
  },
};
</script>

<style lang="sass" scoped>
@import '~Styles/_variables'
.modal
  --modal-title-background-color: #{$gray-90}
  --modal-content-small-padding: 24px
  position: relative
  width: 50%
  height: 50%
  background-color: $white
  overflow: hidden
  text-align: left
  pointer-events: auto

  .modal__content
      padding: 24px
      padding-top: 0
  &.is--full
    width: 100%
    height: 100%
    padding: 0

    .modal__container
      width: 100%
      max-width: 100%
      height: 100%
      padding-top: 0
      animation: fade-in-up 0.3s
      background-color: $gray-10
      &.is--no-animation
        animation: none
      .modal__content
        padding: 60px 40px 110px
        @media screen and (max-width: 767px)
          padding: 45px 12px 100px
  &.is--small
    .modal__container
      max-width: 500px
      @media screen and (max-width: 767px)
        border-bottom-right-radius: 0
        border-bottom-left-radius: 0

    .modal__content
      padding: var(--modal-content-small-padding)
      padding-top: 0

  &.is--large,
  &.is--small
    display: flex
    align-items: center
    justify-content: center
    @media screen and (max-width: 767px)
      align-items: flex-end
    &.scale-in-center-leave-active
      .modal__container
        animation: scale-out-center 0.3s cubic-bezier(0.250, 0.460, 0.450, 0.940) both
        @media screen and (max-width: 767px)
          animation: fade-in-out 0.3s
    .modal__container
      animation: scale-in-center 0.3s cubic-bezier(0.250, 0.460, 0.450, 0.940) both
      box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.30), 0px 6px 10px 4px rgba(0, 0, 0, 0.15)
      @media screen and (max-width: 767px)
        animation: fade-in-up 0.3s
      .modal__header-solid
        margin-bottom: 24px
    .modal__content
      scrollbar-width: none
      -ms-overflow-style: none

      &::-webkit-scrollbar
        display: none
  &.is--no-header
    & .modal__close-button
      fill: $gray-90
  &__backdrop
    position: fixed
    z-index: 100
    overflow-y: auto
    top: 0
    right: 0
    bottom: 0
    left: 0
    width: 100%
    height: 100%
    background-color: rgba($gray-100, .5)
    backdrop-filter: blur(6.7957px)

  &__container
    flex-direction: column
    height: auto
    overflow: hidden
    position: relative
    z-index: 999
    background-color: $white
    max-width: 946px
    width: 100%
  &__header
    padding: 24px 24px 16px
    text-align: center
    position: relative
    top: 0
    left: 0
    right: 0
    z-index: 2
    width: 100%
    display: flex
    align-items: center
  &__header-title
    margin-bottom: 0
    flex: 1
    + .modal__close-button
      position: absolute
      right: 30px
  &__header-solid
    background-color: var(--modal-title-background-color)
    box-shadow: 0 1px 15px 0 rgb(0 0 0 / 5%)
    padding: 14px 30px
    @media screen and (max-width: 767px)
      padding: 14px 12px
    & .modal__close-button
      align-self: center
      fill: $white
      @media screen and (max-width: 767px)
        right: 12px
    & .modal__header-title
      color: $white
      margin: 0 30px
  &__content
    overflow-y: scroll
    overflow-x: hidden
    height: 100%
    margin: 0 auto

  &__close-button
    cursor: pointer
    margin-left: auto
    align-self: flex-start

  &__close-button-icon
      width: 20px
      height: 20px
.fade-in-up-enter-active
  transition: all .3s ease
.fade-in-up-leave-active
  transition: all 0.2s ease

.fade-in-up-enter-to,
.fade-in-up-leave-from
  transform: translateY(0%)

.fade-in-up-enter-from,
.fade-in-up-enter,
.fade-in-up-leave-to,
.fade-in-up-leave
  transform: translateY(100%)

.scale-in-center-enter-to,
.scale-in-center-leave-from
  opacity: 0

.scale-in-center-enter-from,
.scale-in-center-enter,
.scale-in-center-leave-to
  opacity: 1

.scale-in-center-leave-active
  transition: all 0.2s ease

@keyframes fade-in-up
  0%
    transform: translateY(100%)
  100%
    transform: translateY(0%)
@keyframes fade-in-out
  0%
    transform: translateY(0%)
  100%
    transform: translateY(100%)
@keyframes scale-in-center
  0%
    transform: scale(0)
  100%
    transform: scale(1)

@keyframes scale-out-center
  0%
    transform: scale(1)
  100%
    transform: scale(0)
</style>

<style lang="sass">
// body scroll lock
html[class*='is--modal-open']
  overflow: hidden
  pointer-events: none
  body
    scrollbar-width: none
    -ms-overflow-style: none
    &::-webkit-scrollbar
      display: none
</style>
